import { PlusIcon } from "@heroicons/react/outline";
import { useTranslation } from "next-i18next";
import React from "react";

import { OutlinedButton } from "@/features/common/components/button/OutlinedButton";
import { getIsOwner } from "@/features/common/helper/getIsOwner";
import { FollowUserButton } from "@/features/follow/components/followUserButton/FollowUserButton";
import { UnfollowUserButton } from "@/features/follow/components/followUserButton/UnfollowUserButton";
import { FollowUserButtonProps } from "@/features/follow/components/followUserButton/type";
import { useUserContext } from "@/features/user/contexts/useUserContext";
import { useFollowUserDocQuery } from "@/generated/generated-hooks";

export const FollowUserButtonView: React.FC<FollowUserButtonProps> = (
  props,
) => {
  const { writerId, fullWidth, isOnOverlay, utilityClassName = "h-30" } = props;
  const { user, ready, ssrUserId } = useUserContext();
  const isOwner = getIsOwner({ userId: user?._id, ready, ssrUserId, writerId });

  const { data, loading, error } = useFollowUserDocQuery({
    variables: { input: writerId },
    skip: !user || isOwner,
    context: { batch: true },
  });

  const { t } = useTranslation("common");
  const isFollowed = !!data?.followUserDoc;

  if (isOwner) return null;

  /* Loading & Error will disable button */
  if (loading || error)
    return (
      <OutlinedButton
        variant="secondary"
        buttonProps={{ disabled: true }}
        fullWidth={fullWidth}
        utilityClassName={utilityClassName}
      >
        <PlusIcon className="w-4 h-4 mr-1" />
        {t("common:follow.buttonLabel.follow")}
      </OutlinedButton>
    );

  /* if user has already followed the writer */
  if (isFollowed)
    return (
      <UnfollowUserButton
        writerId={writerId}
        isOnOverlay={isOnOverlay}
        fullWidth={fullWidth}
        utilityClassName={utilityClassName}
      />
    );

  /* if user is not following the writer */
  return (
    <FollowUserButton
      writerId={writerId}
      isOnOverlay={isOnOverlay}
      fullWidth={fullWidth}
      utilityClassName={utilityClassName}
    />
  );
};
